import React, { ReactElement } from "react"
import { Box, Progress, ThemeProvider, Typography } from "@carrotfertility/carotene-core"
import { Form, FormAlert, FormOnSubmitHandler } from "@carrotfertility/carotene-core-x"
import { getConditionsCacheKey } from "components/employees/hooks/useEmployeeConditionAttributes"
import { getEmployeeAllCarrotPlansLink } from "components/employees/utils/navigationUtils"
import { useQueryClient } from "react-query"
import { useHistory } from "react-router-dom"
import { Employee } from "types/Employee"
import { useCarrotPlanMappings } from "./hooks/useCarrotPlanMappings"
import { CarrotPlanErrorMessage, CarrotPlanFormProps, CarrotPlanFormStep } from "./CarrotPlanFormTypes"
import { makeAbsoluteUrl } from "./utils/urlUtils"
import { getCarrotPlansCacheKey } from "./hooks/useCarrotPlans"
import useCreateCarrotPlan from "./hooks/useCreateCarrotPlan"
import { useTrackCarrotPlanCreation } from "./hooks/useTrackCarrotPlanCreation"
import { useGetBenefitEnabledJourneysForEmployee } from "./hooks/useGetJourneys"
import useGetMemberJourney from "../employees/hooks/useGetMemberJourney"
import { hasJourneyChanged } from "./JourneyChangeConfirmation"
import { arrangeStepRows } from "./utils/stepHelpers"
import { useInitialJourneyValues } from "./hooks/useInitialJourneyValues"
import { CarrotPlanFormContentWithDynamicOptions } from "./CarrotPlanFormContentWithDynamicOptions"
import { NONE_VALUE } from "./JourneySelection"

export default function CarrotPlanForm({ employee }: { employee: Employee }): ReactElement {
  const history = useHistory()
  const queryClient = useQueryClient()
  const [showJourneyChangeConfirmation, setShowJourneyChangeConfirmation] = React.useState(false)
  const [inReview, setInReview] = React.useState(false)
  const [errorMessage, setErrorMessage] = React.useState("")
  const { trackCarrotPlanCreationComplete } = useTrackCarrotPlanCreation({ employeeId: employee.employeeId })
  const { mutateAsync, isLoading: createCarrotPlanIsLoading } = useCreateCarrotPlan()
  const {
    data: benefitEnabledJourneyData,
    isLoading: isBenefitJourneysLoading
  } = useGetBenefitEnabledJourneysForEmployee(employee.employeeId)
  const { isLoading: isMemberJourneyLoading, data: memberJourney } = useGetMemberJourney(employee.employeeId)
  const initialJourneyValues = useInitialJourneyValues(memberJourney, benefitEnabledJourneyData)

  const {
    mappedFormMenuItems: initialMenuItems,
    mappedFormTopics: initialTopics,
    areMappingsLoading: initialOptionsLoading
  } = useCarrotPlanMappings(employee)

  const invalidateQueries = (): void => {
    const cacheKeys = [getCarrotPlansCacheKey(employee.employeeId), getConditionsCacheKey(employee.employeeId)]
    cacheKeys.map((key) => queryClient.invalidateQueries(key))
  }

  const onSubmit: FormOnSubmitHandler<CarrotPlanFormProps> = async ({
    steps,
    primaryJourney,
    primaryJourneyPhase,
    secondaryJourney,
    secondaryJourneyPhase,
    tertiaryJourney,
    tertiaryJourneyPhase
  }: CarrotPlanFormProps) => {
    setErrorMessage("")
    steps = steps.filter((step) => step.menuItem)
    if (steps.length < 1) {
      setErrorMessage(CarrotPlanErrorMessage.NEEDS_STEP)
      return
    }

    if (!inReview) {
      if (hasJourneyChanged(memberJourney, benefitEnabledJourneyData, primaryJourney, primaryJourneyPhase)) {
        setShowJourneyChangeConfirmation(true)
        return
      }

      setInReview(true)
    } else {
      let recommendedMenuItemCount = 0
      const carrotPlanSteps = steps.map(({ menuItem, content, url }: CarrotPlanFormStep) => {
        const defaultUrl = menuItem?.optionFields?.defaultUrl
        if (menuItem?.optionMetadata?.isRecommended) {
          recommendedMenuItemCount++
        }

        if (!content) {
          return {
            entryId: null,
            url: makeAbsoluteUrl(url, defaultUrl),
            menuItemId: menuItem?.optionFields?.entityId,
            isAssistantRecommended: menuItem?.optionMetadata?.isRecommended
          }
        }

        return {
          entryId: content?.optionFields?.entryId,
          url: makeAbsoluteUrl(content?.optionFields.link, defaultUrl),
          menuItemId: menuItem?.optionFields?.entityId,
          isAssistantRecommended: content?.optionMetadata?.isRecommended
        }
      })
      const journeyData = {
        primaryJourneyId: primaryJourney === NONE_VALUE ? null : primaryJourney,
        primaryJourneyStageId: primaryJourneyPhase === NONE_VALUE ? null : primaryJourneyPhase,
        secondaryJourneyId: secondaryJourney === NONE_VALUE ? null : secondaryJourney,
        secondaryJourneyStageId: secondaryJourneyPhase === NONE_VALUE ? null : secondaryJourneyPhase,
        tertiaryJourneyId: tertiaryJourney === NONE_VALUE ? null : tertiaryJourney,
        tertiaryJourneyStageId: tertiaryJourneyPhase === NONE_VALUE ? null : tertiaryJourneyPhase
      }
      const newValues = {
        employeeId: employee.employeeId,
        carrotPlanSteps,
        journeyData
      }

      if (!createCarrotPlanIsLoading) {
        await mutateAsync(newValues, {
          onSuccess: () => {
            trackCarrotPlanCreationComplete({ recommendedItemsUsed: recommendedMenuItemCount })
            invalidateQueries()
            history.push(getEmployeeAllCarrotPlansLink(`${employee.employeeId}`))
          },
          onError: (error) => {
            setErrorMessage(error.message)
          }
        })
      }
    }
  }

  const onBack = (): void => {
    setInReview(!inReview)
  }

  const onCancel = (): void => {
    history.push(getEmployeeAllCarrotPlansLink(`${employee.employeeId}`))
  }

  const handleJourneyChangeConfirm = (): void => {
    setShowJourneyChangeConfirmation(false)
    setInReview(true)
  }

  const handleJourneyChangeCancel = (): void => {
    setShowJourneyChangeConfirmation(false)
  }

  if (isMemberJourneyLoading || isBenefitJourneysLoading || initialOptionsLoading) {
    return (
      <ThemeProvider>
        <Box paddingTop={(theme) => theme.spacing(theme.tokens.spacing.lg)}>
          <Progress />
        </Box>
      </ThemeProvider>
    )
  }

  return (
    <ThemeProvider>
      {errorMessage && (
        <Box width={"100%"}>
          <FormAlert severity="error">
            <Typography variant="body2">{errorMessage}</Typography>
          </FormAlert>
        </Box>
      )}
      <Form
        mode="onSubmit"
        reValidateMode="onChange"
        onSubmit={onSubmit}
        defaultValues={{
          steps: arrangeStepRows([], []),
          primaryJourney: initialJourneyValues.primaryJourney,
          primaryJourneyPhase: initialJourneyValues.primaryJourneyPhase,
          secondaryJourney: NONE_VALUE,
          secondaryJourneyPhase: NONE_VALUE,
          tertiaryJourney: NONE_VALUE,
          tertiaryJourneyPhase: NONE_VALUE
        }}
        shouldFocusError={false}
      >
        <CarrotPlanFormContentWithDynamicOptions
          employee={employee}
          benefitEnabledJourneyData={benefitEnabledJourneyData}
          inReview={inReview}
          onBack={onBack}
          onCancel={onCancel}
          initialMenuItems={initialMenuItems}
          initialTopics={initialTopics}
          showJourneyChangeConfirmation={showJourneyChangeConfirmation}
          memberJourney={memberJourney}
          handleJourneyChangeConfirm={handleJourneyChangeConfirm}
          handleJourneyChangeCancel={handleJourneyChangeCancel}
        />
      </Form>
    </ThemeProvider>
  )
}
